import * as React from "react";
import clsx from "classnames";

import { makeStyles } from "@material-ui/styles";
import Divider from "@material-ui/core/Divider";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";

import ErrorIcon from "@material-ui/icons/ErrorOutline";

import SizeBreakup from "./SizeBreakup";
import CutPlanSettings from "./CutPlanSettings";

import { OState } from "../../../utils/types";
import { colorTomatoRed } from "../../../utils/colors";
import { isEmpty } from "../../../utils/helpers";

import useView from "./useView";
import MarkerDetails from "./MarkerDetails";
import FabricUsageSummary from "./FabricUsageSummary";
import CutSummary from "./CutSummary";
import ShadeSummary from "./ShadeSummary";
import Footer from "./Footer";
import { useSetting } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import MuiTable from "../../../components/MuiTable";
import { LotType } from "../../../constants";
import VarianceReport from "./VarianceReport";

const useStyles = makeStyles(theme => ({
  root: {},
  container: {
    margin: "1.5em",
    padding: 0
  },
  mrTop: {
    marginTop: "1.5em"
  },
  card: {
    padding: "0",
    boxShadow: theme.shadows[1],
    backgroundColor: "white"
  },
  margin: {
    margin: "1em 1.5em"
  },
  padding: {
    padding: "1em"
  },
  sidePadding: {
    padding: "0 1.5em"
  },
  bottomPadding: {
    paddingBottom: "0.5em"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  }
}));

const CutPlanView = props => {
  const classes = useStyles();
  const { roles, hasAccess, hasPermission } = props;
  const {
    version,
    order,
    flowInfoColumns,
    flowInfoRows,
    cutOrder,
    cutOrderList,
    cutOrderId,
    fabricPatternList,
    ledgerList,
    rollList,
    scheduledTasks,
    flowTabs,
    planTabs,
    flowTabIdx,
    planTabIdx,
    setCutOrderId,
    onTabChange,
    onRefresh,
    unitList
  } = useView(props);

  const onRowClick = row => {
    if (isEmpty(row.parentKey)) {
      setCutOrderId(row.key);
    }
  };

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, "orders");

  const isPreRun = cutOrder?.lotType === LotType.PRE_RUN;
  const finalGroupingCompleted = cutOrder?.stateValue >= OState.FINAL_GROUPING_COMPLETED.stateValue;
  //   const cutPlanGenerated = cutOrder?.stateValue > OState.FINAL_GROUPING_COMPLETED.stateValue;
  //   const feedbackStarted = cutOrder?.stateValue >= OState.MAIN_FEEDBACK_IN_PROGRESS.stateValue;
  const isRejected = cutOrderList.some(flow => flow.orderConfig?.cutPlanApproval === "Rejected");
  const isFlowRejected = cutOrder?.orderConfig?.cutPlanApproval === "Rejected";

  const uom = flowInfoRows.find(e => e.cutOrder?.id === cutOrderId)?.uom;

  const selectedFlowInfo = flowInfoRows.find(e => e.cutOrder?.id === cutOrderId);

  // const defaultExpanded = flowInfoRows.filter(fi => isEmpty(fi.parentKey)).length === 1;
  const defaultExpanded = false;

  return (
    <div className={clsx(classes.root)}>
      <div style={{ padding: "1em 1.5em" }}>
        <Typography variant="h5">Cut Plan</Typography>
      </div>

      {isRejected && (
        <div className={classes.container}>
          <Alert classes={{ message: classes.alert }} icon={<ErrorIcon fontSize="large" />} severity="error">
            <AlertTitle
              style={{
                fontSize: "1.2em"
              }}>
              {cutOrderList?.length === 1
                ? `Cut Plan Rejected by ${cutOrder?.orderConfig?.cutPlanApprovedBy}`
                : "Some CutPlans are rejected by Merchants"}
            </AlertTitle>
            {cutOrderList?.length === 1 ? cutOrder?.orderConfig?.merchantRemarks : "Review individual Cut Plan tab for rejection details"}
          </Alert>
        </div>
      )}

      <div className={classes.sidePadding}>
        <div>
          <MuiTable
            // key={version}
            title="Cut Order"
            toolbar={true}
            idKey="key"
            parentIdKey="parentKey"
            columns={flowInfoColumns}
            rows={flowInfoRows}
            defaultExpanded={defaultExpanded}
            expandedColor={["#BFDDF8", "#7AB9F0"]}
            i18nKey="resources.grouping.fields.flowInfo"
            onRowClick={onRowClick}
            rowStyle={({ row }) => ({
              backgroundColor: row?.key === cutOrderId ? "#BFDDF8" : "none"
            })}
          />
        </div>

        <div
          className={clsx({
            [classes.mrTop]: true,
            [classes.card]: flowTabs.length > 1,
            [classes.bottomPadding]: flowTabs.length > 1
          })}>
          {flowTabs.length > 1 && (
            <Tabs value={flowTabIdx} onChange={(e, idx) => onTabChange("flow", idx)} variant="scrollable" scrollButtons="auto">
              {flowTabs.map((tab, idx) => (
                <Tab key={idx} label={tab?.label} />
              ))}
            </Tabs>
          )}
          {flowTabs.length > 1 && <Divider />}
          <div
            className={clsx({
              [classes.mrTop]: flowTabs.length === 1,
              [classes.margin]: planTabs.length > 1 && flowTabs.length > 1,
              [classes.card]: planTabs.length > 1,
              [classes.bottomPadding]: planTabs.length > 1
            })}>
            {planTabs.length > 1 && (
              <Tabs value={planTabIdx} onChange={(e, idx) => onTabChange("plan", idx)} variant="scrollable" scrollButtons="auto">
                {planTabs.map((tab, idx) => (
                  <Tab key={idx} label={tab} />
                ))}
              </Tabs>
            )}
            {planTabs.length > 1 && <Divider />}

            <div
              className={clsx({
                [classes.padding]: flowTabs?.length > 1 || planTabs?.length > 1
              })}>
              {isFlowRejected && cutOrderList?.length > 1 && (
                <div className={classes.container}>
                  <Alert classes={{ message: classes.alert }} icon={<ErrorIcon fontSize="large" />} severity="error">
                    <AlertTitle style={{ fontSize: "1.2em" }}>{`Cut Plan Rejected by ${cutOrder?.orderConfig?.cutPlanApprovedBy}`}</AlertTitle>
                    {cutOrder?.orderConfig?.merchantRemarks}
                  </Alert>
                </div>
              )}

              {/** Size Breakup */}
              <SizeBreakup order={order} cutOrder={cutOrder} />

              {!isEmpty(cutOrderList) && !isPreRun && !finalGroupingCompleted && (
                <div className={clsx(classes.displayCenter, classes.mrTop)}>
                  <Typography variant="h6" style={{ color: colorTomatoRed.main }}>
                    Final Grouping not completed yet
                  </Typography>
                </div>
              )}

              <CutPlanSettings
                className={classes.mrTop}
                order={order}
                cutOrder={cutOrder}
                scheduledTasks={scheduledTasks}
                selectedFlowInfo={selectedFlowInfo}
                roles={roles}
                hasAccess={hasAccess}
                onRefresh={onRefresh}
              />

              <FabricUsageSummary
                className={classes.mrTop}
                order={order}
                cutOrder={cutOrder}
                uom={uom}
                rollInfoList={rollList}
                ledgerList={ledgerList}
                fabricPatternList={fabricPatternList}
                planTabIdx={planTabIdx}
                roles={roles}
                hasAccess={hasAccess}
              />

              <CutSummary className={classes.mrTop} order={order} cutOrder={cutOrder} planTabIdx={planTabIdx} />

              <MarkerDetails
                className={classes.mrTop}
                order={order}
                cutOrder={cutOrder}
                scheduledTasks={scheduledTasks}
                planTabIdx={planTabIdx}
                flowInfoColumns={flowInfoColumns}
                ledgerList={ledgerList}
                roles={roles}
                hasAccess={hasAccess}
                onRefresh={onRefresh}
                unitList={unitList}
              />

              <VarianceReport
                className={classes.mrTop}
                roles={roles}
                order={order}
                cutOrder={cutOrder}
                uom={uom}
                rollInfoList={rollList}
                ledgerList={ledgerList}
                fabricPatternList={fabricPatternList}
                onRefresh={onRefresh}
                planTabIdx={planTabIdx}
              />

              <ShadeSummary className={classes.mrTop} order={order} cutOrder={cutOrder} planTabIdx={planTabIdx} />

              <Footer
                version={version}
                order={order}
                cutOrder={cutOrder}
                uom={uom}
                cutOrderList={cutOrderList}
                rollInfoList={rollList}
                ledgerList={ledgerList}
                scheduledTasks={scheduledTasks}
                planTabIdx={planTabIdx}
                orderFields={orderFields}
                roles={roles}
                hasAccess={hasAccess}
                hasPermission={hasPermission}
                onRefresh={onRefresh}
                fabricPatternList={fabricPatternList}
                unitList={unitList}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CutPlanView;
